// Klargo Design System — Profile (/Profile), restyled to match
// Klargo-Reference/src/pages/Profile.tsx: AuthShell, one sheet per section,
// identity (name/email/BankID) stated once at the top, Help written as a
// route rather than a document row. All data fetching, field validation and
// API calls are unchanged from the previous (MUI) implementation.
//
// Lives under src/public/ (not src/pages/Profile/) because the ds Tailwind
// build only scans src/public/**/*.{js,jsx} — see scripts/public-tailwind
// .config.cjs. A page outside that path compiles with none of its ds-*/
// layout utility classes generated, which is silently invisible (no build
// error) but breaks every bit of spacing/alignment/width on the page.
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { CheckCircle2, HelpCircle, Plus, Download } from "lucide-react";

import ApiCall from "../../Apicall/ApiCall";
import AuthShell from "../components/ds/AuthShell";
import { useBankIdVerification, isVerifiedProfile } from "../components/auth/BankIdVerification";
import { ForwardArrow } from "../components/ds/StatusMark";
import { Button } from "../components/ui/button";
import { formatPersonName, formatFullName } from "../lib/formatName";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "../components/ui/dropdown-menu";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;

const maskPersonalId = (ssn) => {
  if (!ssn || ssn === "" || ssn.toLowerCase() === "string") return "—";
  const clean = ssn.replace(/\D/g, "");
  if (clean.length >= 8) return `${clean.slice(0, 8)}-****`;
  return "—";
};

const maskBankAccount = (acct) => {
  if (!acct) return null;
  const last4 = acct.slice(-4);
  return `•••• •••• •••• ${last4}`;
};

// ── Presentational building blocks (local, ds-scope grammar) ───────────────
const Section = ({ title, id, note, route, children }) => (
  <section id={id} className="mt-[34px]">
    <div className="mb-3.5 flex items-baseline justify-between gap-2.5 border-b-2 border-ds-ink pb-2">
      <h2 className="ds-title-component m-0">{title}</h2>
    </div>
    {route && <div className="-mt-2 mb-2 flex flex-col items-start">{route}</div>}
    <div className="rounded-xl border border-ds-sheet-edge bg-ds-sheet">
      <div className="divide-y divide-ds-rule-row">{children}</div>
    </div>
    {note && <p className="ds-meta m-0 mt-2.5 max-w-[58ch]">{note}</p>}
  </section>
);

const FieldRow = ({ label, value, action, mono }) => (
  <div className="flex min-h-[52px] items-center justify-between gap-4 px-5 py-3">
    <span className="ds-label shrink-0">{label}</span>
    <div className="flex min-w-0 items-center justify-end gap-3">
      <span
        className={`truncate text-[15px] font-medium text-ds-ink ${
          mono ? "ds-mono text-[14px] tracking-[0.04em] tabular-nums" : ""
        }`}
      >
        {value}
      </span>
      {action}
    </div>
  </div>
);

const LEGAL_ROW =
  "block px-5 py-3.5 text-[15px] text-ds-body no-underline transition-colors hover:text-ds-ink";

const LinkAction = ({ onClick, children, withIcon, ariaLabel }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={ariaLabel}
    className="inline-flex shrink-0 items-center gap-1 rounded text-sm font-semibold text-ds-ink transition-colors hover:text-ds-green"
  >
    {withIcon && <Plus className="h-4 w-4" aria-hidden="true" />}
    <span className="ds-underline">{children}</span>
  </button>
);

const InlineEditRow = ({
  label,
  displayValue,
  missingLabel,
  editing,
  onEdit,
  inputId,
  inputType = "text",
  value,
  onChange,
  onSave,
  onCancel,
  error,
  placeholder,
  saveLabel,
  cancelLabel,
  editLabel,
  editWithIcon,
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  if (editing) {
    return (
      <div className="space-y-2.5 px-5 py-4">
        <label htmlFor={inputId} className="ds-label block">
          {label}
        </label>
        <input
          id={inputId}
          ref={inputRef}
          type={inputType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-invalid={!!error}
          className="ds-input"
        />
        {error && <p className="ds-error-text m-0">{error}</p>}
        <div className="flex items-center justify-end gap-2">
          <Button type="button" size="sm" className="h-9" onClick={onSave}>
            {saveLabel}
          </Button>
          <Button type="button" size="sm" variant="ghost" className="h-9" onClick={onCancel}>
            {cancelLabel}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[52px] items-center justify-between gap-4 px-5 py-3">
      <span className="ds-label shrink-0">{label}</span>
      <div className="flex min-w-0 items-center justify-end gap-3">
        {displayValue ? (
          <span className="truncate text-[15px] font-medium text-ds-ink">{displayValue}</span>
        ) : (
          <span className="truncate text-[15px] font-normal text-ds-muted">{missingLabel}</span>
        )}
        <button
          type="button"
          onClick={onEdit}
          aria-label={`${editLabel} ${label.toLowerCase()}`}
          className="inline-flex shrink-0 items-center gap-1 rounded text-sm font-semibold text-ds-ink transition-colors hover:text-ds-green"
        >
          {editWithIcon && <Plus className="h-4 w-4" aria-hidden="true" />}
          <span className="ds-underline">{editLabel}</span>
        </button>
      </div>
    </div>
  );
};

// ── Close account — 2-step confirm, same final action as before (mailto) ──
const CloseAccountModal = ({ open, onOpenChange, t }) => {
  const [step, setStep] = useState(1);
  const [confirmText, setConfirmText] = useState("");
  const confirmKeyword = t("profile.closeAccount.confirmKeyword");

  useEffect(() => {
    if (!open) {
      setStep(1);
      setConfirmText("");
    }
  }, [open]);

  const handleDownloadClick = () => {
    Swal.fire({
      icon: "info",
      title: t("profile.closeAccount.downloadToastTitle"),
      text: t("profile.closeAccount.downloadToastDesc"),
      timer: 2200,
      showConfirmButton: false,
      toast: true,
      position: "top-end",
    });
  };

  const handleCloseAccount = () => {
    onOpenChange(false);
    window.location.href =
      "mailto:info@klargo.se?subject=" + encodeURIComponent(t("profile.closeAccount.title"));
  };

  const isConfirmValid = confirmText === confirmKeyword;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="ds-scope border-ds-rule-edge bg-ds-sheet font-ds-sans text-ds-body sm:max-w-md">
        {step === 1 ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-base font-semibold text-ds-ink">
                {t("profile.closeAccount.title")}
              </DialogTitle>
              <DialogDescription className="pt-2 text-sm text-ds-text">
                {t("profile.closeAccount.description")}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <ul className="space-y-3 text-sm text-ds-body">
                {[1, 2, 3].map((n) => (
                  <li key={n} className="flex gap-3">
                    <span className="shrink-0 text-ds-muted">•</span>
                    <span>{t(`profile.closeAccount.item${n}`)}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={handleDownloadClick}
                className="mt-1 flex items-center gap-2 rounded text-sm font-semibold text-ds-ink transition-colors hover:text-ds-green"
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                <span className="ds-underline">{t("profile.closeAccount.downloadDocs")}</span>
              </button>
            </div>
            <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="ghost" className="flex-1 sm:flex-none" onClick={() => onOpenChange(false)}>
                {t("actions.cancel")}
              </Button>
              <Button type="button" variant="default" className="flex-1 sm:flex-none" onClick={() => setStep(2)}>
                {t("actions.continue")}
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-base font-semibold text-ds-ink">
                {t("profile.closeAccount.confirmTitle")}
              </DialogTitle>
              <DialogDescription className="pt-2 text-sm text-ds-text">
                {t("profile.closeAccount.confirmDescription")}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label htmlFor="confirm-delete" className="text-sm text-ds-text">
                  {t("profile.closeAccount.confirmLabel").split("{keyword}")[0]}
                  <span className="font-semibold text-ds-ink">{confirmKeyword}</span>
                  {t("profile.closeAccount.confirmLabel").split("{keyword}")[1]}
                </label>
                <input
                  id="confirm-delete"
                  type="text"
                  className="ds-input ds-input--mono"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={confirmKeyword}
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="ghost" className="flex-1 sm:flex-none" onClick={() => setStep(1)}>
                {t("actions.back")}
              </Button>
              <Button
                type="button"
                variant="destructive"
                className="flex-1 sm:flex-none"
                onClick={handleCloseAccount}
                disabled={!isConfirmValid}
              >
                {t("profile.closeAccount.confirmButton")}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// PROFILE PAGE
// ═══════════════════════════════════════════════════════════════════════════
export default function ProfilePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isSv = i18n.language?.startsWith("sv");

  // ── Data state (unchanged) ─────────────────────────────────────────────
  const [pageLoading, setPageLoading] = useState(true);
  const [profile, setProfile] = useState({
    name: "",
    surname: "",
    fullName: "",
    emailAddress: "",
    personalContact: "",
    bankDetail: "",
    bankIdSSN: "",
    validCarLicense: false,
    isBankIdVerified: false,
  });
  // An e-mail account can verify its identity here at any time (it is asked anyway before a deal).
  const { ensureVerified, verificationDialogs } = useBankIdVerification();
  const [reloadTick, setReloadTick] = useState(0);

  // ── Edit states ────────────────────────────────────────────────────────
  const [editingEmail, setEditingEmail] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  const [editingBank, setEditingBank] = useState(false);

  // ── Form values ────────────────────────────────────────────────────────
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [bankAccount, setBankAccount] = useState("");
  const [showBankAccount, setShowBankAccount] = useState(false);

  // ── UI state ───────────────────────────────────────────────────────────
  const [closeAccountOpen, setCloseAccountOpen] = useState(false);

  // ── Fetch data (unchanged) ────────────────────────────────────────────
  useEffect(() => {
    let active = true;
    (async () => {
      setPageLoading(true);
      try {
        const sessionRes = await ApiCall({
          url: "/Session/GetCurrentLoginInformations",
          method: "GET",
        });
        const uid = sessionRes?.data?.result?.user?.id;
        if (!uid) {
          navigate("/Home", { replace: true });
          return;
        }
        if (!active) return;

        const profRes = await ApiCall({
          url: "/User/GetUserProfileById",
          method: "GET",
          params: { Id: uid },
        });
        const u = profRes?.data?.result || {};
        if (!active) return;

        const bankDetailClean =
          u.bankDetail && u.bankDetail === u.bankIdSSN ? "" : u.bankDetail || "";

        setProfile({
          name: formatPersonName(u.name) || "",
          surname: formatPersonName(u.surname) || "",
          fullName: formatFullName(u.name, u.surname) || formatPersonName(u.fullName) || "",
          emailAddress: u.emailAddress || "",
          personalContact: u.personalContact || "",
          bankDetail: bankDetailClean,
          bankIdSSN: u.bankIdSSN || "",
          validCarLicense: u.validCarLicense === true,
          isBankIdVerified: isVerifiedProfile(u),
        });
        setEmail(u.emailAddress || "");
        setPhone(u.personalContact || "");
        setBankAccount(bankDetailClean);
      } catch (err) {
        navigate("/Home", { replace: true });
      } finally {
        if (active) setPageLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [navigate, reloadTick]);

  // ── UpdateMyProfile helper (unchanged) ────────────────────────────────
  const updateMyProfile = async (patch) => {
    const payload = {
      name: profile.name,
      surname: profile.surname,
      emailAddress: profile.emailAddress,
      personalContact: profile.personalContact,
      bankDetail: profile.bankDetail,
      validCarLicense: profile.validCarLicense,
      ...patch,
    };
    // A BankID account may have no e-mail yet: send none rather than an empty (invalid) one.
    if (!payload.emailAddress) payload.emailAddress = null;
    const res = await ApiCall({
      url: "/User/UpdateMyProfile",
      method: "PUT",
      data: payload,
    });
    return res?.data?.success === true;
  };

  // ── Computed ───────────────────────────────────────────────────────────
  const effectiveBank = bankAccount || profile.bankDetail || "";
  const hasBankAccount = !!effectiveBank && effectiveBank !== profile.bankIdSSN;
  const hasLicense = profile.validCarLicense;

  const maskedPersonalId = maskPersonalId(profile.bankIdSSN);
  const displayBankAccount = bankAccount
    ? showBankAccount
      ? bankAccount
      : maskBankAccount(bankAccount)
    : null;

  // ── Email save (unchanged) ────────────────────────────────────────────
  const handleSaveEmail = async () => {
    if (!email) {
      setEmailError(t("profile.validation.emailRequired"));
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setEmailError(t("profile.validation.emailInvalid"));
      return;
    }
    try {
      const ok = await updateMyProfile({ emailAddress: email });
      if (ok) {
        setProfile((p) => ({ ...p, emailAddress: email }));
        setEditingEmail(false);
        setEmailError("");
        Swal.fire({
          icon: "success",
          title: t("profile.toast.emailUpdated"),
          text: t("profile.toast.emailUpdatedDesc"),
          timer: 2000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      } else {
        throw new Error("Update returned false");
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: isSv ? "Fel" : "Error",
        text: isSv ? "Kunde inte spara e-post." : "Could not save email.",
      });
    }
  };

  // ── Phone save (unchanged) ────────────────────────────────────────────
  const handleSavePhone = async () => {
    if (!phone) {
      setPhoneError(t("profile.validation.phoneRequired"));
      return;
    }
    if (!PHONE_RE.test(phone)) {
      setPhoneError(t("profile.validation.phoneInvalid"));
      return;
    }
    try {
      const ok = await updateMyProfile({ personalContact: phone });
      if (ok) {
        setProfile((p) => ({ ...p, personalContact: phone }));
        setEditingPhone(false);
        setPhoneError("");
        Swal.fire({
          icon: "success",
          title: t("profile.toast.phoneUpdated"),
          text: t("profile.toast.phoneUpdatedDesc"),
          timer: 2000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      } else {
        throw new Error();
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: isSv ? "Fel" : "Error",
        text: isSv ? "Kunde inte spara telefonnummer." : "Could not save phone number.",
      });
    }
  };

  // ── Bank save (unchanged) ─────────────────────────────────────────────
  const handleSaveBank = async () => {
    try {
      const ok = await updateMyProfile({ bankDetail: bankAccount });
      if (ok) {
        setProfile((p) => ({ ...p, bankDetail: bankAccount }));
        setEditingBank(false);
        Swal.fire({
          icon: "success",
          title: t("profile.toast.bankUpdated"),
          text: t("profile.toast.bankUpdatedDesc"),
          timer: 2000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      } else {
        throw new Error();
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: isSv ? "Fel" : "Error",
        text: isSv ? "Kunde inte spara bankkonto." : "Could not save bank account.",
      });
    }
  };

  // ── License toggle (unchanged) ────────────────────────────────────────
  const handleLicenseToggle = async (checked) => {
    try {
      const ok = await updateMyProfile({ validCarLicense: checked });
      if (ok) {
        setProfile((p) => ({ ...p, validCarLicense: checked }));
        Swal.fire({
          icon: "success",
          title: checked ? t("profile.licenseToast.attested") : t("profile.licenseToast.removed"),
          text: checked
            ? t("profile.licenseToast.attestedDesc")
            : t("profile.licenseToast.removedDesc"),
          timer: 2000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      }
    } catch {
      // revert if failed — no-op, UI stays optimistic
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════════════
  if (pageLoading) {
    return (
      <div className="ds-scope flex min-h-[60vh] items-center justify-center bg-ds-canvas">
        <div
          className="h-8 w-8 animate-spin rounded-full border-2 border-ds-rule-edge"
          style={{ borderTopColor: "var(--ds-green)" }}
          role="status"
          aria-label={t("profile.pageTitle")}
        />
      </div>
    );
  }

  const helpRoute = (
    <Link to="/support" data-testid="profile-help-support" className="ds-action-text">
      {t("profile.menu.helpSupport")}
      <ForwardArrow stroke="currentColor" width={14} height={11} />
    </Link>
  );

  return (
    <AuthShell t={t} activeKey="profile">
      <section className="ds-animate-page ds-auth-rail max-w-[680px] pb-[var(--ds-page-bottom)] pt-[var(--ds-page-top)]">
        {/* Identity: name is the primary line, email secondary, verification
            a restrained semantic state — never a tile or avatar. */}
        <div className="mb-2 text-[13px] font-medium text-ds-muted">{t("profile.pageTitle")}</div>
        <h1 className="ds-title-route m-0 mb-1.5">{profile.fullName || "—"}</h1>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {profile.emailAddress && (
            <span className="text-sm text-ds-text">{profile.emailAddress}</span>
          )}
          {profile.isBankIdVerified ? (
            <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ds-green-deep">
              <CheckCircle2 className="h-3.5 w-3.5 text-ds-green" aria-hidden="true" />
              {t("profile.status.bankidVerified")}
            </span>
          ) : (
            <span className="inline-flex flex-wrap items-center gap-x-2 text-[13px] font-medium text-ds-muted">
              {t("profile.status.bankidNotVerified")}
              <button
                type="button"
                onClick={async () => { if (await ensureVerified()) setReloadTick((n) => n + 1); }}
                className="ds-underline font-semibold text-ds-ink"
              >
                {t("verify.verifyWithBankId")}
              </button>
            </span>
          )}
        </div>
        {verificationDialogs}

        {/* GROUP 1 — Personal information: facts Klargo does not own. */}
        <Section title={t("profile.section.personal")} note={t("profile.bankidInfo")}>
          <FieldRow label={t("profile.field.name")} value={profile.fullName || "—"} />
          <FieldRow label={t("profile.field.personalId")} value={maskedPersonalId} mono />
        </Section>

        {/* GROUP 2 — Contact information: everything here is editable. */}
        <Section title={t("profile.section.contact")}>
          <InlineEditRow
            label={t("profile.field.email")}
            displayValue={profile.emailAddress}
            missingLabel={t("profile.field.notProvided")}
            editing={editingEmail}
            onEdit={() => setEditingEmail(true)}
            inputId="email"
            inputType="email"
            value={email}
            onChange={(v) => {
              setEmail(v);
              if (emailError) setEmailError(EMAIL_RE.test(v) ? "" : t("profile.validation.emailInvalid"));
            }}
            onSave={handleSaveEmail}
            onCancel={() => {
              setEmail(profile.emailAddress);
              setEmailError("");
              setEditingEmail(false);
            }}
            error={emailError}
            placeholder="din@mejl.se"
            saveLabel={t("actions.save")}
            cancelLabel={t("actions.cancel")}
            editLabel={profile.emailAddress ? t("profile.action.change") : t("profile.action.add")}
            editWithIcon={!profile.emailAddress}
          />
          <InlineEditRow
            label={t("profile.field.phone")}
            displayValue={profile.personalContact}
            missingLabel={t("profile.field.notProvided")}
            editing={editingPhone}
            onEdit={() => setEditingPhone(true)}
            inputId="phone"
            inputType="tel"
            value={phone}
            onChange={(v) => {
              setPhone(v);
              if (phoneError) setPhoneError(PHONE_RE.test(v) ? "" : t("profile.validation.phoneInvalid"));
            }}
            onSave={handleSavePhone}
            onCancel={() => {
              setPhone(profile.personalContact);
              setPhoneError("");
              setEditingPhone(false);
            }}
            error={phoneError}
            placeholder="+46 70 123 45 67"
            saveLabel={t("actions.save")}
            cancelLabel={t("actions.cancel")}
            editLabel={profile.personalContact ? t("profile.action.change") : t("profile.action.add")}
            editWithIcon={!profile.personalContact}
          />
        </Section>

        {/* GROUP 3 — Driver's license and payout */}
        <Section title={t("profile.section.verificationPayout")}>
          <div id="license-section" className="px-5 py-4">
            <div className="flex items-baseline justify-between gap-4">
              <h3 className="m-0 text-sm font-semibold text-ds-ink">{t("profile.section.license")}</h3>
              {hasLicense && (
                <span className="text-sm font-medium text-ds-ink">{t("profile.status.attested")}</span>
              )}
            </div>

            <div className="mt-3 flex items-start gap-3">
              <input
                id="license-confirm"
                type="checkbox"
                checked={hasLicense}
                onChange={(e) => handleLicenseToggle(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-ds-rule-edge"
                style={{ accentColor: "var(--ds-green)" }}
              />
              <label
                htmlFor="license-confirm"
                className="cursor-pointer text-sm font-normal leading-relaxed text-ds-body"
              >
                {t("profile.licenseCheckbox")}
              </label>
            </div>

            {!hasLicense && <p className="ds-meta m-0 ml-7 mt-3">{t("profile.licenseHint")}</p>}

            <div className="ml-7 mt-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-1 rounded text-[13px] text-ds-muted transition-colors hover:text-ds-green"
                  >
                    <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
                    {t("profile.licenseWhy")}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="ds-scope max-w-xs border-ds-rule-edge bg-ds-sheet p-3 font-ds-sans text-sm text-ds-body"
                >
                  {t("profile.licenseWhyInfo")}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div id="payout-section" className="px-5 py-4">
            <h3 className="m-0 mb-3 text-sm font-semibold text-ds-ink">
              {t("profile.status.payoutAccount")}
            </h3>
            {editingBank ? (
              <div className="space-y-2.5">
                <label htmlFor="bankAccount" className="ds-label block">
                  {t("profile.field.bankAccount")}
                </label>
                <input
                  id="bankAccount"
                  type="text"
                  autoFocus
                  className="ds-input ds-input--mono"
                  value={bankAccount}
                  onChange={(e) => setBankAccount(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSaveBank();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      setBankAccount(profile.bankDetail);
                      setEditingBank(false);
                    }
                  }}
                  placeholder="SE12 3456 7890 1234 5678 90"
                />
                <div className="flex items-center justify-end gap-2">
                  <Button type="button" size="sm" className="h-9" onClick={handleSaveBank}>
                    {t("actions.save")}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-9"
                    onClick={() => {
                      setBankAccount(profile.bankDetail);
                      setEditingBank(false);
                    }}
                  >
                    {t("actions.cancel")}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
                <span className="ds-mono break-all text-[14px] font-medium tracking-[0.04em] text-ds-ink tabular-nums">
                  {displayBankAccount ?? (
                    <span className="font-ds-sans text-sm font-normal tracking-normal text-ds-muted">
                      {t("profile.field.notProvided")}
                    </span>
                  )}
                </span>
                <div className="flex shrink-0 items-center justify-end gap-3">
                  {displayBankAccount && (
                    <button
                      type="button"
                      onClick={() => setShowBankAccount((v) => !v)}
                      aria-label={`${
                        showBankAccount ? t("profile.action.hide") : t("profile.action.show")
                      } ${t("profile.status.payoutAccount").toLowerCase()}`}
                      className="shrink-0 rounded text-sm font-medium text-ds-text transition-colors hover:text-ds-green"
                    >
                      {showBankAccount ? t("profile.action.hide") : t("profile.action.show")}
                    </button>
                  )}
                  {hasBankAccount ? (
                    <LinkAction
                      onClick={() => setEditingBank(true)}
                      ariaLabel={`${t("profile.action.change")} ${t(
                        "profile.status.payoutAccount"
                      ).toLowerCase()}`}
                    >
                      {t("profile.action.change")}
                    </LinkAction>
                  ) : (
                    <LinkAction onClick={() => setEditingBank(true)} withIcon>
                      {t("profile.action.addBankAccount")}
                    </LinkAction>
                  )}
                </div>
              </div>
            )}
            <p className="ds-meta m-0 mt-3">{t("profile.bankInfo")}</p>
          </div>
        </Section>

        {/* GROUP 4 — Help (a route), legal documents, close account. */}
        <Section title={t("profile.section.accountLegal")} route={helpRoute}>
          <Link to="/villkor" className={LEGAL_ROW}>
            {t("profile.legal.terms")}
          </Link>
          <Link to="/integritet" className={LEGAL_ROW}>
            {t("profile.legal.privacy")}
          </Link>
        </Section>

        <section className="mt-[34px]">
          <button
            type="button"
            onClick={() => setCloseAccountOpen(true)}
            className="rounded text-sm font-semibold text-ds-red hover:underline"
          >
            {t("profile.action.closeAccount")}
          </button>
          <p className="ds-meta m-0 mt-1.5 max-w-[52ch]">{t("profile.closeAccountHint")}</p>
        </section>

        <CloseAccountModal open={closeAccountOpen} onOpenChange={setCloseAccountOpen} t={t} />
      </section>
    </AuthShell>
  );
}
